import { useLayoutEffect, useRef, type ReactNode } from 'react'
import { NavLink } from 'react-router'
import { cx } from '../design/cx'
import { LIVE_TAB, NAV_TABS } from './navTabs'
import { useAuth } from '../auth/AuthProvider'
import { UserMenu } from '../auth/UserMenu'
import { Pentacle } from '../design/components'
import { BuildInfo, StatusBar } from './StatusBar'
import { AssistantDock } from './assistant/AssistantDock'
import { LiveAnnouncement } from '../domains/live/LiveAnnouncement'
import { useLiveSessions } from '../domains/live/queries'
import styles from './AppShell.module.css'

export interface AppShellProps {
  /** Display-face title — character name, scene name. Rendered in Cinzel. */
  title: string
  /** All-caps line under the title, e.g. "WIZARD · THE WINTER KNIGHT". */
  subtitle?: string | undefined
  /**
   * Portrait for the header. Replaces the pentacle sigil when present — the spec's
   * header is an identity block, and a face identifies a character better than a
   * generic mark does.
   */
  avatarUrl?: string | null | undefined
  /**
   * A secondary identity line under the title — the character's full name, when it
   * says something the title does not. Kept to one short line so the header does
   * not eat the top of a 390px screen.
   */
  identity?: string | null | undefined
  /**
   * Left-hand header slot, before the identity block. For a control that belongs to the
   * *page* rather than to the app — kept at the opposite end from the primary navigation so
   * the two are never read as one row of tabs.
   */
  headerLeading?: ReactNode
  /** Right-hand header slot (shape picker, GM badge). */
  headerAction?: ReactNode
  children: ReactNode
}

/**
 * Sticky identity header carrying the primary navigation, scrolling content below;
 * 16px gutters, single column.
 *
 * The navigation lives in the header at every width. It was a bottom tab bar that became a
 * left rail — the spec's §4 layout — but both put it a long way from where the eye already
 * is, and the rail spent 220px of a desktop window on three links. In the header it sits
 * beside the title on one line: below 600px the labels drop and the icons carry it, above
 * they come back.
 *
 * There is still exactly ONE <nav>, restyled at the breakpoint rather than a second element
 * that the other one hides. Two navigation landmarks with the same label is a genuine
 * accessibility fault, and it also means assistive tech and tests have to guess which of the
 * two is the live one.
 *
 * The remaining breakpoint is additive: ≥600px a two-column card grid, opt in per screen.
 */
export function AppShell({
  title,
  subtitle,
  avatarUrl,
  identity,
  headerLeading,
  headerAction,
  children,
}: AppShellProps) {
  const { status } = useAuth()
  const shell = useRef<HTMLDivElement>(null)
  const header = useRef<HTMLElement>(null)

  const signedIn = status === 'authenticated'

  /**
   * The Live tab appears only when there is something live to go to.
   *
   * A permanent fifth tab that is empty six evenings out of seven costs every other screen the
   * width, and the announcement banner is what actually brings someone here. This query does
   * **not** poll (see `useLiveSessions`): the hub's `SessionStarted` invalidates it, so on a
   * normal screen it is one request at sign-in and nothing after.
   */
  const live = useLiveSessions(signedIn)
  const tabs = (live.data ?? []).length > 0 ? [...NAV_TABS, LIVE_TAB] : NAV_TABS

  /**
   * Publish the header's *measured* height as `--header-h`, for anything below that pins
   * itself under it.
   *
   * `--header-height` is a design token — one number, 64px — and the header is only that
   * tall when it fits on one row. On a phone the navigation wraps and it is nearer 120px,
   * so a sticky element offset by the token slides underneath and is lost behind it. The
   * measurement is the only honest answer, and it changes with the viewport, with a long
   * character name, and with the safe-area inset.
   */
  useLayoutEffect(() => {
    const element = header.current
    const root = shell.current
    if (element === null || root === null || typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(() => {
      root.style.setProperty('--header-h', `${element.getBoundingClientRect().height}px`)
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  return (
    <div className={styles.shell} ref={shell}>
      <header className={styles.header} ref={header}>
        {headerLeading !== undefined && <div className={styles.headerLeading}>{headerLeading}</div>}
        {avatarUrl !== null && avatarUrl !== undefined && avatarUrl !== '' ? (
          <img className={styles.headerPortrait} src={avatarUrl} alt="" />
        ) : (
          <Pentacle size={26} className={styles.headerSigil} />
        )}
        <div className={styles.headerText}>
          <h1 className={styles.title}>{title}</h1>
          {identity !== null && identity !== undefined && identity !== '' && (
            <p className={styles.identity}>{identity}</p>
          )}
          {subtitle !== undefined && <p className={styles.subtitle}>{subtitle}</p>}
        </div>
        {/* Navigation and account travel together, so on a narrow screen they wrap onto a
            second line as one block and leave the name the full width of the first. Split
            up, the name got a third of the row and broke across three lines. */}
        <div className={styles.headerTrailing}>
          <nav className={styles.nav} aria-label="Primary">
            {tabs.map((tab) => (
              <NavLink
                key={tab.to}
                to={tab.to}
                className={({ isActive }) => cx(styles.link, isActive && styles.linkActive)}
              >
                <span className={styles.icon}>{tab.icon}</span>
                {/* Below 600px the label is hidden visually but stays in the accessibility
                    tree — an icon-only link still has to say what it is. */}
                <span className={styles.linkLabel}>{tab.label}</span>
              </NavLink>
            ))}
          </nav>

          {headerAction !== undefined && <div className={styles.headerAction}>{headerAction}</div>}
          {status === 'authenticated' && <UserMenu />}
        </div>
      </header>

      {/* Sticky under the header, and absent unless the app has something to say about
          itself: offline, or a new build waiting for permission to take over. */}
      <StatusBar />

      {/* Above the page rather than inside it: a fight starting is news whatever screen you are
          on, and the shell is the only place that is true of. */}
      <LiveAnnouncement enabled={signedIn} />

      <main className={styles.main}>{children}</main>

      {/* The assistant lives in the shell, so it is reachable from every screen and the
          conversation does not restart when the user navigates. */}
      <AssistantDock />

      <BuildInfo />
    </div>
  )
}
